import { useMediaQuery } from '@material-ui/core'
import HelpIcon from '@material-ui/icons/HelpOutlineOutlined'
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined'
import { drawersActions } from 'ducks/drawers'
import { permissionsSelectors } from 'ducks/permissions'
import { CountryAndLocaleMenuButton } from 'expo/CountryAndLocaleMenuButton'
import { ExpoContextProvider } from 'expo/ExpoContext'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { useGlobalCart } from 'hooks/useGlobalCart'
import ActivityButtonIcon from 'layout/widgets/ActivityButtonIcon'
import {
  BaseThemeType,
  Box,
  Chip,
  ComponentVersionsInherit,
  ComponentVersions_3_0,
  IconButton,
  styled,
} from 'opensolar-ui'
import SearchBarController from 'persistentContent/elements/SearchBar'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ActionType, EventType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'

export const NavigationIconButton = styled(IconButton)({
  color: '#4F4D55',
  fontSize: 12,
  fontWeight: 700,
  gap: 6,
  padding: 8,
  '&:hover': {
    color: '#0A090B',
  },
  '& svg': {
    height: 20,
    width: 20,
  },
})

const StyledChip = styled(Chip)(({ theme }: { theme: BaseThemeType }) => ({
  position: 'absolute',
  right: 4,
  top: -5,
  borderRadius: 4,
  background: `${theme.palette.red[800]} !important`,
  '&:hover': {
    background: `${theme.palette.red[800]} !important`,
    backgroundColor: `${theme.palette.red[800]} !important`,
    color: theme.palette.common.white,
  },
  '& .OSUI-Chip-label': {
    color: theme.palette.common.white,
    fontSize: '10px',
    lineHeight: '10px',
    fontWeight: 700,
  },
}))

const NavigationIconButtons = () => {
  const { totalItemCount } = useGlobalCart()
  const history = useHistory()
  const enableMultiDistributorShop = useFeatureFlag('enable_multi_distributor_shop', 'on')
  const hasHardwareOrdering = useFeatureFlag('hardware_ordering', 'on')
  const { allowView: allowViewShop, allowEdit: allowEditShop } = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'shop_hardware_access')
  )
  const dispatch = useDispatch()
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.up('sm'))
  const isInExpoRoute = useMemo(() => {
    const path = history.location.pathname
    return path.includes('/expo') && !path.startsWith('/expo_')
  }, [history.location.pathname])
  const { trackEvent, trackUserInteraction } = useTrackComponent({
    componentKey: 'navigation_icon_buttons',
    eventName: 'User Viewed Navigation Icon Buttons',
  })
  const isActivityCenterOn = useFeatureFlag('actions_activity_centre', 'on')

  const onClickShoppingCart = () => {
    trackUserInteraction(
      { type: ActionType.CLICK },
      {
        eventName: 'Hardware Cart',
        source: 'shop_global_cart',
        action: 'navigate',
      }
    )
    history.push('/shop/cart')
  }

  const showCartIcon = allowViewShop && enableMultiDistributorShop && hasHardwareOrdering

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Box alignItems="center" display="flex" gridGap={6}>
        {isInExpoRoute && (
          <ExpoContextProvider>
            <CountryAndLocaleMenuButton />
          </ExpoContextProvider>
        )}

        {isActivityCenterOn && <ActivityButtonIcon variant="navigation" />}

        <SearchBarController
          triggerIconButton={
            <NavigationIconButton size="small" shape="squared">
              <SearchOutlinedIcon />
            </NavigationIconButton>
          }
        />
        {showCartIcon && allowEditShop && (
          <NavigationIconButton onClick={onClickShoppingCart} size="small" shape="squared">
            <ShoppingCartOutlinedIcon />
            {totalItemCount > 0 && <StyledChip label={`${totalItemCount}`} />}
          </NavigationIconButton>
        )}

        {isTablet && (
          <NavigationIconButton
            onClick={() => {
              trackEvent(
                EventType.USER_INTERACTION,
                { type: ActionType.CLICK },
                { eventName: 'User Opened Help Center' }
              )
              dispatch(drawersActions.setRightDrawerOpen('helpCenter'))
            }}
            size="small"
            shape="squared"
            data-side-panel-trigger="help-center"
          >
            <HelpIcon />
          </NavigationIconButton>
        )}
      </Box>
    </ComponentVersionsInherit>
  )
}

export default NavigationIconButtons
